import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigationType } from 'react-router-dom';
import './App.css';
import { api } from './api';
import AdminPage from './AdminPage';
import AuthPage from './AuthPage';
import { BlogIndex, BlogPost, LearnPage } from './Blog';
import FirmPage from './FirmPage';
import PropFirmsPage from './PropFirmsPage';
import ComparePage from './ComparePage';
import TemplatePage from './TemplatePage';
import JourneyLanding from './JourneyLanding';
import FreeReportCard from './FreeReportCard';
import { COMPARE_PAGES } from './comparePages';
import { FIRM_PAGES } from './firmPages';
import { ToolPage, ToolsIndex } from './Tools';
import ContactPage from './ContactPage';
import Journal from './Journal';
import Landing from './Landing';
import { PrivacyPage, RefundPage, TermsPage } from './Legal';
import PricingPage from './PricingPage';
import IbkrGuidePage from './IbkrGuidePage';
import { ForgotPasswordPage, ResetPasswordPage } from './PasswordReset';
import { NotFoundPage } from './PublicPage';
import ErrorBoundary from './ErrorBoundary';
import { routinePageForNow } from './Routine';
import { I18nProvider } from './i18n';
import CookieBanner from './CookieBanner';
import ChatBubble from './Chat';
import { GuidePage, GuideSlideIn } from './Guide';
import { setNoTrack, trackDemo, trackSignup, usePageTracking } from './analytics';
import SharePage from './SharePage';
import ConnectPage, { AFTER_LOGIN_KEY } from './ConnectPage';

// The public course page loads with the course itself, only when it's opened.
const AcademyLanding = lazy(() => import('./AcademyLanding'));

// Where to go after logging in: back to the "Connect your AI" consent page when that's where the
// trader came from (ConnectPage.js), otherwise Home.
function afterLogin() {
  try {
    const next = sessionStorage.getItem(AFTER_LOGIN_KEY);
    if (next && next.startsWith('/connect')) { sessionStorage.removeItem(AFTER_LOGIN_KEY); return next; }
  } catch { /* Home */ }
  return '/app/home';
}

// Counts page views on every route (inside the router so it sees route changes).
function PageTracker() {
  usePageTracking();
  return null;
}

// A link to another page starts at the top, like a normal website; Back keeps the
// browser's own scroll position, and links to a #section are left alone.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const type = useNavigationType();
  useEffect(() => {
    if (type !== 'POP' && !hash) window.scrollTo(0, 0);
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Sends an old address to its new one, keeping what's after the "?".
function MovedTo({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

function App() {
  // undefined while checking for an existing session, null when logged out
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api('/auth/me')
      .then(setUser)
      .catch(() => setUser(null));
    // A new account made with Google (google.js) lands here: count the sign-up, then tidy the address.
    const url = new URL(window.location.href);
    if (url.searchParams.get('signup') === 'google') {
      trackSignup('google');
      url.searchParams.delete('signup');
      window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    }
  }, []);

  const handleSessionExpired = useCallback(() => setUser(null), []);

  // Opens the visitor's own copy of the sample account, on the account's rules panel.
  const startDemo = useCallback(async (lang) => {
    const demoUser = await api('/demo/start', {
      method: 'POST',
      body: { lang, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
    });
    try {
      if (demoUser.demo && demoUser.demo.accountId) window.localStorage.setItem('mm-selected-account', demoUser.demo.accountId);
    } catch {
      // Private mode: the journal opens on "All accounts" instead.
    }
    trackDemo();
    setUser(demoUser);
    return demoUser;
  }, []);

  async function handleLogout() {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setUser(null);
    }
  }

  // Admins' own browsing isn't counted in the analytics.
  if (user && user.isAdmin) setNoTrack(true);

  if (user === undefined) return <div className="loading">…</div>;

  return (
    <I18nProvider user={user} onUserChange={setUser}>
    <BrowserRouter>
      <ScrollToTop />
      <PageTracker />
      <CookieBanner />
      <ErrorBoundary>
      <Routes>
        {/* Members go to their own home page; visitors (and the demo) see the public homepage. */}
        <Route path="/" element={user && !user.isDemo ? <Navigate to="/app/home" replace /> : <Landing user={user} onStartDemo={startDemo} />} />
        <Route path="/es" element={user && !user.isDemo ? <Navigate to="/app/home" replace /> : <Landing user={user} forceLang="es" onStartDemo={startDemo} />} />
        <Route path="/blog" element={<BlogIndex user={user} />} />
        <Route path="/blog/:slug" element={<BlogPost user={user} />} />
        <Route path="/es/blog" element={<BlogIndex user={user} forceLang="es" />} />
        <Route path="/es/blog/:slug" element={<BlogPost user={user} forceLang="es" />} />
        <Route path="/learn" element={<LearnPage user={user} />} />
        <Route path="/prop-firms" element={<PropFirmsPage user={user} />} />
        <Route path="/es/prop-firms" element={<PropFirmsPage user={user} forceLang="es" />} />
        <Route path="/trading-course" element={<Suspense fallback={null}><AcademyLanding user={user} /></Suspense>} />
        <Route path="/es/trading-course" element={<Suspense fallback={null}><AcademyLanding user={user} forceLang="es" /></Suspense>} />
        <Route path="/my-trading-journey" element={<JourneyLanding user={user} onStartDemo={startDemo} />} />
        <Route path="/es/my-trading-journey" element={<JourneyLanding user={user} forceLang="es" onStartDemo={startDemo} />} />
        <Route path="/trading-journal-template" element={<TemplatePage user={user} />} />
        <Route path="/es/trading-journal-template" element={<TemplatePage user={user} forceLang="es" />} />
        <Route path="/report-card" element={<FreeReportCard user={user} />} />
        <Route path="/share/:token" element={<SharePage />} />
        <Route path="/es/report-card" element={<FreeReportCard user={user} forceLang="es" />} />
        {COMPARE_PAGES.flatMap((p) => [
          <Route key={p.slug} path={`/${p.slug}`} element={<ComparePage user={user} slug={p.slug} />} />,
          <Route key={`es-${p.slug}`} path={`/es/${p.slug}`} element={<ComparePage user={user} slug={p.slug} forceLang="es" />} />,
        ])}
        {FIRM_PAGES.flatMap((p) => [
          <Route key={p.slug} path={`/${p.slug}`} element={<FirmPage user={user} slug={p.slug} />} />,
          <Route key={`es-${p.slug}`} path={`/es/${p.slug}`} element={<FirmPage user={user} slug={p.slug} forceLang="es" />} />,
        ])}
        <Route path="/es/learn" element={<LearnPage user={user} forceLang="es" />} />
        <Route path="/guide" element={<GuidePage user={user} />} />
        <Route path="/es/guide" element={<GuidePage user={user} forceLang="es" />} />
        <Route path="/tools" element={<ToolsIndex user={user} />} />
        <Route path="/tools/:slug" element={<ToolPage user={user} />} />
        <Route path="/es/tools" element={<ToolsIndex user={user} forceLang="es" />} />
        <Route path="/es/tools/:slug" element={<ToolPage user={user} forceLang="es" />} />
        <Route
          path="/login"
          element={user && !user.isDemo ? <Navigate to={afterLogin()} replace /> : <AuthPage mode="login" onAuthenticated={setUser} />}
        />
        <Route
          path="/signup"
          element={user && !user.isDemo ? <Navigate to={afterLogin()} replace /> : <AuthPage mode="signup" onAuthenticated={setUser} />}
        />
        <Route path="/connect" element={<ConnectPage user={user} />} />
        <Route path="/app/routine" element={<Navigate to={`/app/${routinePageForNow()}`} replace />} />
        <Route path="/app/business" element={<Navigate to="/app/journey/money" replace />} />
        {/* The course, Practice and Backtest moved to Matrix Academy. */}
        {[['course', 'course'], ['practice', 'chart-lab'], ['backtest', 'edge-lab']].map(([from, to]) => <Route key={from} path={`/app/${from}`} element={<MovedTo to={`/academy/${to}`} />} />)}
        {[
          ['/app/home', 'home'], ['/app/dashboard', 'dashboard'], ['/app', 'journal'], ['/app/morning', 'morning'], ['/app/session', 'session'], ['/app/reflection', 'reflection'],
          ['/app/goals', 'goals'], ['/app/notebook', 'notebook'], ['/app/insights', 'insights'], ['/app/settings', 'settings'], ['/app/plans', 'plans'],
          ['/app/playbook', 'playbook'], ['/app/compare', 'compare'], ['/app/market-calendar', 'market'], ['/academy', 'academy'], ['/academy/progress', 'academy-progress'], ['/academy/course', 'course'], ['/academy/chart-lab', 'practice'], ['/academy/edge-lab', 'backtest'], ['/app/accounts', 'accounts'], ['/app/accounts/new', 'account-new'], ['/app/mark', 'mark'], ['/app/journey', 'journey'], ['/app/journey/timeline', 'timeline'], ['/app/journey/progress', 'progress'], ['/app/journey/edge', 'edge'], ['/app/journey/milestones', 'milestones'], ['/app/journey/recap', 'recap'], ['/app/journey/money', 'money'], ['/app/journey/profitable', 'jmoney'], ['/app/journey/better', 'jbetter'], ['/app/journey/edge-milestones', 'jgrowth'], ['/app/journey/identity', 'jidentity'],
        ].map(([path, view]) => (
          <Route
            key={path}
            path={path}
            element={
              user ? (
                <Journal
                  user={user}
                  view={view}
                  onLogout={handleLogout}
                  onUserChange={setUser}
                  onSessionExpired={handleSessionExpired}
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        ))}
        <Route
          path="/admin"
          element={
            !user ? (
              <Navigate to="/login" replace />
            ) : user.isAdmin ? (
              <AdminPage onLogout={handleLogout} onSessionExpired={handleSessionExpired} />
            ) : (
              <Navigate to="/app" replace />
            )
          }
        />
        <Route path="/contact" element={<ContactPage user={user} />} />
        <Route path="/terms" element={<TermsPage user={user} />} />
        <Route path="/privacy" element={<PrivacyPage user={user} />} />
        <Route path="/refunds" element={<RefundPage user={user} />} />
        <Route path="/pricing" element={<PricingPage user={user} />} />
        <Route path="/es/pricing" element={<PricingPage user={user} forceLang="es" />} />
        <Route path="/guides/interactive-brokers" element={<IbkrGuidePage user={user} />} />
        <Route path="/es/guides/interactive-brokers" element={<IbkrGuidePage user={user} forceLang="es" />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage onAuthenticated={setUser} />} />
        <Route path="*" element={<NotFoundPage user={user} />} />
      </Routes>
      </ErrorBoundary>
      <GuideSlideIn user={user} />
      <ChatBubble user={user} />
    </BrowserRouter>
    </I18nProvider>
  );
}

export default App;
